import React from 'react';
import clsx from 'clsx';
import { Images, SendConfiguration, StakeholderType } from '@urbanx/agx-ui-components';
import {
  AgxButton,
  AgxRadioGroup,
  AgxSpacer,
  AgxToggleSwitch,
  AgxTypography,
} from '@urbanx/agx.ui-components2';
import PreviewSignatories from './PreviewSignatories';
import { PreparedSignatory } from 'utils/vendorBuyerUtil';
import {
  getOrderedStakeholders,
  getSigningOrderGroups,
  SigningOrderOption,
} from './SendConfirmation.helpers';

interface SendConfirmationProps {
  buyers?: PreparedSignatory[];
  buyerSolicitor?: PreparedSignatory;
  vendors?: PreparedSignatory[];
  vendorSolicitor?: PreparedSignatory;
  onBack: () => void;
  onSend: (
    sendVendorsNow: boolean,
    signingOrderGroups?: StakeholderType[],
    includeSolicitorsInSalesAdvice?: boolean
  ) => void;
  sendViaDocuSign?: boolean;
  disabled?: boolean;
  sendConfiguration?: SendConfiguration;
  isMobile?: boolean;
  enableSigningOrderSelection?: boolean;
  enableSalesAdviceSolicitorSelection?: boolean;
}

const SendConfirmation: React.FC<SendConfirmationProps> = ({
  buyers = [],
  buyerSolicitor,
  vendors = [],
  vendorSolicitor,
  onBack,
  onSend,
  sendViaDocuSign = true,
  disabled = false,
  sendConfiguration,
  isMobile = false,
  enableSigningOrderSelection = false,
  enableSalesAdviceSolicitorSelection = false,
}) => {
  const [selectedSigningOrder, setSelectedSigningOrder] = React.useState<SigningOrderOption>(
    SigningOrderOption.BuyersFirst
  );
  const [includeSolicitorsInSalesAdvice, setIncludeSolicitorsInSalesAdvice] = React.useState(true);
  const signingOrderGroups = getSigningOrderGroups(selectedSigningOrder);
  const stakeholders =
    enableSigningOrderSelection && signingOrderGroups
      ? getOrderedStakeholders(sendConfiguration?.stakeholders ?? [], signingOrderGroups)
      : sendConfiguration?.stakeholders;

  return (
    <AgxSpacer direction="vertical" gap="spacing-xl">
      {!enableSigningOrderSelection && sendConfiguration?.subtitle && (
        <AgxTypography id="send-confirmation-subtitle" type="label" size="large">
          {sendConfiguration.subtitle}
        </AgxTypography>
      )}
      {enableSigningOrderSelection && (
        <AgxSpacer direction="vertical" padding={{ bottom: 'md' }}>
          <AgxRadioGroup
            id="contractExchangeSigningOrder"
            label="Signing order"
            layout="vertical"
            value={selectedSigningOrder}
            options={[
              {
                label: 'Send to Buyer(s) first',
                value: SigningOrderOption.BuyersFirst,
              },
              {
                label: 'Send to Vendor(s) first',
                value: SigningOrderOption.VendorsFirst,
              },
              {
                label: 'No signing order',
                value: SigningOrderOption.NoSigningOrder,
              },
            ]}
            onChange={(value) => {
              setSelectedSigningOrder(value as SigningOrderOption);
            }}
          />
        </AgxSpacer>
      )}
      {enableSigningOrderSelection && (
        <AgxTypography
          id="send-confirmation-subtitle"
          type="body_ui_strong"
          size="medium"
          color="var(--text-primary)"
        >
          Signatories
        </AgxTypography>
      )}
      <PreviewSignatories
        buyers={buyers}
        buyerSolicitor={buyerSolicitor}
        vendors={vendors}
        vendorSolicitor={vendorSolicitor}
        stakeholders={stakeholders}
      />
      {enableSalesAdviceSolicitorSelection && (
        <AgxSpacer
          direction="horizontal"
          contentAlignment="center"
          gap="sm"
          padding={{ bottom: 'spacing-5xl' }}
        >
          <AgxToggleSwitch
            id="includeSolicitorsInSalesAdvice"
            checked={includeSolicitorsInSalesAdvice}
            onChange={setIncludeSolicitorsInSalesAdvice}
          />
          <AgxTypography
            id="includeSolicitorsInSalesAdvice-label"
            type="label"
            size="medium"
            htmlFor="includeSolicitorsInSalesAdvice"
          >
            Email solicitors a copy of the sales advice
          </AgxTypography>
        </AgxSpacer>
      )}
      {sendConfiguration?.informationText && (
        <AgxSpacer direction="horizontal" fill className="signatory-row-information">
          <AgxSpacer
            direction="vertical"
            fill
            contentJustification="center"
            className={clsx('signatory-content', {
              veryLargeSidePadding: !isMobile,
            })}
          >
            <AgxSpacer direction="horizontal" contentAlignment="center" gap="spacing-lg">
              <Images.InfoOutline />
              <AgxTypography
                id="send-confirmation-information-text"
                type="label"
                size="small"
                color="var(--neutral-grey-700)"
              >
                {sendConfiguration?.informationText}
              </AgxTypography>
            </AgxSpacer>
          </AgxSpacer>
        </AgxSpacer>
      )}
      <AgxSpacer
        direction="vertical"
        padding={{
          top: 'spacing-3xl',
          right: 'spacing-xl',
          bottom: 'spacing-xl',
          left: 'spacing-xl',
        }}
        className={clsx('signatory-row-buttons', {
          borderTop: sendConfiguration?.informationText || enableSalesAdviceSolicitorSelection,
        })}
      >
        <AgxSpacer direction="horizontal" contentJustification="center" gap="spacing-lg">
          <AgxButton
            id="cancel"
            size="large"
            variant="hollow"
            text="Back"
            onClick={() => onBack()}
            disabled={disabled}
          />
          <AgxButton
            id="startsubmission"
            variant="primary"
            size="large"
            text="Send"
            onClick={() =>
              onSend(
                sendConfiguration?.sendVendorsNow ?? true,
                enableSigningOrderSelection ? signingOrderGroups : undefined,
                enableSalesAdviceSolicitorSelection ? includeSolicitorsInSalesAdvice : undefined
              )
            }
            disabled={disabled}
          />
        </AgxSpacer>
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default SendConfirmation;
